/**
 * env.js — Environment Variables & Logging
 *
 * PURPOSE:
 *   Provides build-time environment placeholders and runtime utilities.
 *   Exports API URL, widget URLs, and debug logging functions.
 *
 * HOW IT WORKS:
 *   Constants are replaced at build time by esbuild define.
 *   getTenantId() reads data-tenant from script tag.
 *   log/warn/error only output when DEBUG is enabled.
 *
 * BE DEPENDENCY:
 *   None
 *
 * USAGE:
 *   import { API__BASE_URL, log, getTenantId } from './core/env.js';
 */

// These values are injected by build.js banner before this module loads
// If not already set (by banner), use placeholder strings
const API__BASE_URL = typeof __API_BASE_URL__ !== 'undefined' ? __API_BASE_URL__ : "__API_BASE_URL__";
const WIDGET_BASE_URL = typeof __WIDGET_BASE_URL__ !== 'undefined' ? __WIDGET_BASE_URL__ : "__WIDGET_BASE_URL__";
const ASSETS_BASE_URL = typeof __ASSETS_BASE_URL__ !== 'undefined' ? __ASSETS_BASE_URL__ : "__ASSETS_BASE_URL__";
const APP_ENV = typeof __APP_ENV__ !== 'undefined' ? __APP_ENV__ : "__APP_ENV__";
const DEBUG = typeof __DEBUG__ !== 'undefined' ? __DEBUG__ : "__DEBUG__";
// Build-time-injected git branch + branch-mapped tenant (build.js detectDevTenant).
// Both empty when git is unavailable or branch doesn't match the convention.
const DEV_TENANT = typeof __DEV_TENANT__ !== 'undefined' ? __DEV_TENANT__ : "";
const DEV_BRANCH = typeof __DEV_BRANCH__ !== 'undefined' ? __DEV_BRANCH__ : "";

export { API__BASE_URL, WIDGET_BASE_URL, ASSETS_BASE_URL, APP_ENV, DEBUG, DEV_TENANT, DEV_BRANCH };

// Tenant ID resolution order (dev/staging only — production always returns
// null so the BE identifies tenant via the Origin header):
//   1. ?tenant=… query param   (per-tab override for ad-hoc testing)
//   2. data-tenant="…" on the loader <script> (explicit embed)
//   3. __DEV_TENANT__ injected at build time from the current git branch
//      (e.g. feature/widget-goosecross-variant → "goosecross"). Lets the
//      tracked test.html "just work" when you switch branches — no manual
//      data-tenant edits per branch.
export function getTenantId() {
  if (APP_ENV === "production") {
    return null;
  }

  if (typeof window !== "undefined" && window.location && window.location.search) {
    const override = new URLSearchParams(window.location.search).get("tenant");
    if (override) {
      // Local test may use ?tenant=halleckvineyard/vineyard-tours/ — keep only
      // the tenant id segment before any path.
      return override.trim().split("/")[0];
    }
  }

  const script = document.currentScript || document.querySelector("script[data-tenant]");
  const fromScript = script ? script.getAttribute("data-tenant") : null;
  if (fromScript) return fromScript;

  if (typeof __DEV_TENANT__ !== "undefined" && __DEV_TENANT__) {
    return __DEV_TENANT__;
  }

  return null;
}

// Reads the logged-in shopper id the host page injects onto the widget loader
// <script> tag (data-customer-id), mirroring getTenantId's data-tenant read.
// Unlike getTenantId this is NOT gated on env: the host injects it in every
// environment (the new widget is decoupled from Shopify page globals — see
// PENDING_FOGARTY_PARITY.md). Returns null when absent (treated as logged-out).
export function getCustomerId() {
  const script =
    document.currentScript || document.querySelector("script[data-customer-id]");
  return script ? script.getAttribute("data-customer-id") || null : null;
}

export function log(...args) {
  if (DEBUG === true || DEBUG === "true") {
    console.log("[RedConcierge]", ...args);
  }
}

export function warn(...args) {
  if (DEBUG === true || DEBUG === "true") {
    console.warn("[RedConcierge]", ...args);
  }
}

export function error(...args) {
  console.error("[RedConcierge]", ...args);
}